// src/utils/mluPairingUtils.js

/**
 * Shared MLU pairing helpers for T4R Certification mode.
 *
 * Pairing rule: within each framework prefix (EDU, GOV, TEC, EID),
 * consecutive odd-even suffixes form a pair:
 *   x01 <-> x02,  x03 <-> x04,  x05 <-> x06
 */

/**
 * Given an MLU id like "EDU-101", return its paired twin id ("EDU-102").
 * Returns null if the MLU has no pair.
 */
export function getPairedId(id) {
  if (!id || typeof id !== "string") return null;
  const match = id.match(/^([A-Z]{3})-(\d)(\d{2})$/);
  if (!match) return null;
  const [, prefix, module, suffix] = match;
  const num = parseInt(suffix, 10);
  let pairedNum = null;
  if (num % 2 === 1) pairedNum = num + 1; // odd -> even (01->02, 03->04, 05->06)
  else pairedNum = num - 1;               // even -> odd (02->01, 04->03, 06->05)
  if (pairedNum < 1) return null;
  return `${prefix}-${module}${String(pairedNum).padStart(2, "0")}`;
}

/**
 * Group MLUs into pairs for certification mode.
 * Returns an array of arrays: [[mlu1, mlu2], [mlu3, mlu4], [mlu5], ...]
 *
 * Each element in `mlus` must have at least an `.id` property.
 */
export function groupIntoPairs(mlus) {
  const used = new Set();
  const groups = [];
  for (const mlu of mlus) {
    if (used.has(mlu.id)) continue;
    const pairedId = getPairedId(mlu.id);
    const partner = pairedId ? mlus.find((m) => m.id === pairedId) : null;
    if (partner && !used.has(partner.id)) {
      used.add(mlu.id);
      used.add(partner.id);
      // Always put the lower number first
      if (mlu.id < partner.id) groups.push([mlu, partner]);
      else groups.push([partner, mlu]);
    } else {
      used.add(mlu.id);
      groups.push([mlu]);
    }
  }
  return groups;
}
